/**
 * nutri-leaf-hero.js
 * Nutri-Leaf Dynamic Hero Slider — carousel controller.
 * Custom Element (Dawn convention): lifecycle via connectedCallback /
 * disconnectedCallback, so Theme Editor section reloads and removals are
 * handled automatically. Config comes from data attributes rendered by
 * sections/nutri-leaf-hero.liquid.
 *
 * Autoplay behaviour (P1-41):
 *   - Arrow prev/next → temporary pause (user:false), resumes when mouse
 *     leaves or focus exits the hero (unless another pause reason is active).
 *   - Dot click → permanent user pause (user:true), only the rotation
 *     toggle or a page reload resumes autoplay.
 *   - Mouse enter / focus in → temporary pause, resumes on leave/exit.
 *   - Rotation toggle → explicit play/pause, clears user pause.
 *   - OS-level prefers-reduced-motion change → stops or resumes autoplay
 *     (but never overrides a user-initiated dot pause).
 *   - Theme Editor block select → navigates to slide and pauses; block
 *     deselect resumes (same guards as mouse leave).
 */
(function () {
  'use strict';

  class NLHeroSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.slides = Array.from(this.querySelectorAll('[data-nl-hero-slide]'));
      if (this.slides.length < 2) return;
      this.initialized = true;

      this.dots = Array.from(this.querySelectorAll('[data-nl-hero-dot]'));
      this.prevBtn = this.querySelector('[data-nl-hero-prev]');
      this.nextBtn = this.querySelector('[data-nl-hero-next]');
      this.rotationBtn = this.querySelector('[data-nl-hero-rotation]');
      this.rotationPauseIcon = this.querySelector('[data-nl-hero-rotation-icon="pause"]');
      this.rotationPlayIcon = this.querySelector('[data-nl-hero-rotation-icon="play"]');
      this.statusEl = this.querySelector('[data-nl-hero-status]');
      this.current = 0;
      this.timer = null;
      this.userPaused = false;
      this.focusPaused = false;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = (parseInt(this.dataset.autoplaySpeed, 10) || 5) * 1000;
      this.slideAnnouncement = this.dataset.slideAnnouncement || 'Slide {number} of {total}';
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = this.motionQuery.matches;

      // Bound handler references so they can be removed on disconnect
      this.handleClick = this._onClick.bind(this);
      this.handleKeydown = this._onKeydown.bind(this);
      this.handleMouseEnter = this._onMouseEnter.bind(this);
      this.handleMouseLeave = this._onMouseLeave.bind(this);
      this.handleFocusIn = this._onFocusIn.bind(this);
      this.handleFocusOut = this._onFocusOut.bind(this);
      this.handleBlockSelect = this._onBlockSelect.bind(this);
      this.handleBlockDeselect = this._onBlockDeselect.bind(this);
      this.handleMotionChange = this._onMotionChange.bind(this);
      this.handlePrefersReducedMotionChange = this._onPrefersReducedMotionChange.bind(this);

      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      document.addEventListener('nl:motionchange', this.handleMotionChange);
      this.motionQuery.addEventListener('change', this.handlePrefersReducedMotionChange);

      if (this.autoplayEnabled && !this.reducedMotion) {
        this.start();
      }
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.initialized = false;
      clearInterval(this.timer);
      this.timer = null;
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      document.removeEventListener('nl:motionchange', this.handleMotionChange);
      if (this.motionQuery) {
        this.motionQuery.removeEventListener('change', this.handlePrefersReducedMotionChange);
        this.motionQuery = null;
      }
    }

    goTo(index, announce) {
      index = (index + this.slides.length) % this.slides.length;
      if (index === this.current) return;
      if (announce === undefined) announce = true;

      var prevSlide = this.slides[this.current];
      var nextSlide = this.slides[index];

      // Hide previous
      prevSlide.classList.remove('nl-hero__slide--active');
      prevSlide.setAttribute('aria-hidden', 'true');
      prevSlide.setAttribute('hidden', '');

      // Show next
      this.current = index;
      nextSlide.classList.add('nl-hero__slide--active');
      nextSlide.removeAttribute('aria-hidden');
      nextSlide.removeAttribute('hidden');

      // Update dots
      this.dots.forEach(function (dot, i) {
        dot.classList.toggle('nl-hero__dot--active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      // Update live region
      if (announce && this.statusEl) {
        this.statusEl.textContent = this.slideAnnouncement
          .replace('{number}', index + 1)
          .replace('{total}', this.slides.length);
      }
    }

    start() {
      var self = this;
      this.stop();
      this.setAttribute('data-nl-hero-autoplay', 'true');
      this.timer = setInterval(function () {
        self.goTo((self.current + 1) % self.slides.length, false);
      }, this.autoplaySpeed);
      if (this.rotationBtn) {
        this.rotationBtn.setAttribute('aria-pressed', 'false');
        this.rotationBtn.setAttribute('aria-label', this.rotationBtn.getAttribute('data-pause-label') || 'Pause slideshow');
      }
      if (this.rotationPauseIcon) {
        this.rotationPauseIcon.style.display = '';
        if (this.rotationPlayIcon) this.rotationPlayIcon.style.display = 'none';
      }
    }

    stop(opts) {
      opts = opts || {};
      var user = opts.user || false;
      if (user) this.userPaused = true;
      clearInterval(this.timer);
      this.timer = null;
      this.removeAttribute('data-nl-hero-autoplay');
      if (this.rotationBtn) {
        this.rotationBtn.setAttribute('aria-pressed', 'true');
        this.rotationBtn.setAttribute('aria-label', this.rotationBtn.getAttribute('data-play-label') || 'Play slideshow');
      }
      if (this.rotationPlayIcon) {
        this.rotationPlayIcon.style.display = '';
        if (this.rotationPauseIcon) this.rotationPauseIcon.style.display = 'none';
      }
    }

    _onClick(e) {
      var target = e.target.closest('[data-nl-hero-prev]');
      if (target) { this.stop({ user: false }); this.goTo(this.current - 1); return; }
      target = e.target.closest('[data-nl-hero-next]');
      if (target) { this.stop({ user: false }); this.goTo(this.current + 1); return; }
      target = e.target.closest('[data-nl-hero-dot]');
      if (target) { this.stop({ user: true }); this.goTo(parseInt(target.dataset.index, 10)); return; }
      target = e.target.closest('[data-nl-hero-rotation]');
      if (target) {
        if (this.timer) { this.stop({ user: true }); }
        else { this.userPaused = false; this.start(); }
      }
    }

    _onKeydown(e) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        this.stop({ user: false });
        this.goTo(this.current + (e.key === 'ArrowRight' ? 1 : -1));
      }
    }

    _onMouseEnter() {
      if (this.timer) this.stop({ user: false });
    }

    _onMouseLeave() {
      if (!this.timer && !this.userPaused && !this.focusPaused && this.autoplayEnabled && !this.reducedMotion) {
        this.start();
      }
    }

    _onFocusIn() {
      this.focusPaused = true;
      if (this.timer) this.stop({ user: false });
    }

    _onFocusOut() {
      // Resume only when focus leaves the hero entirely
      var self = this;
      setTimeout(function () {
        if (!self.contains(document.activeElement)) {
          self.focusPaused = false;
          if (!self.timer && !self.userPaused && self.autoplayEnabled && !self.reducedMotion) {
            self.start();
          }
        }
      }, 0);
    }

    _onBlockSelect(e) {
      // Navigate to the slide containing the selected block
      if (!e.target.closest('#' + this.id)) return;
      var slide = e.target.closest('.nl-hero__slide');
      if (!slide) return;
      var index = this.slides.indexOf(slide);
      if (index < 0) return;
      this.stop({ user: false });
      this.goTo(index);
    }

    _onBlockDeselect(e) {
      // Resume autoplay when leaving a block — same guards as _onMouseLeave
      if (!e.target.closest('#' + this.id)) return;
      if (!this.userPaused && !this.focusPaused && this.autoplayEnabled && !this.reducedMotion) {
        this.start();
      }
    }

    _onMotionChange(e) {
      if (e.detail.paused) {
        if (this.timer) this.stop({ user: false });
      } else if (!this.userPaused && this.autoplayEnabled && !this.reducedMotion) {
        this.start();
      }
    }

    _onPrefersReducedMotionChange(e) {
      this.reducedMotion = e.matches;
      if (e.matches) {
        if (this.timer) this.stop({ user: false });
      } else if (!this.userPaused && !this.focusPaused && this.autoplayEnabled) {
        this.start();
      }
    }
  }

  if (!customElements.get('nl-hero-slider')) {
    customElements.define('nl-hero-slider', NLHeroSlider);
  }
})();
